import { AnyObject, Optional } from '@vimeo/core/types/generic';

/**
 * Logger file that logs only in dev/flagged environment.
 * Allows to collect app logs in a file for future investigations or dev sharing.
 */
export class Logger {
  public static LOG_FILE_BUFFER: Array<string> = [];
  public static LOG_FILE_BUFFER_LINES_LIMIT: number = 5000;
  public static LOG_FILE_BUFFER_SPLICE: number = 100;

  /**
   * Shortcut to prevent complex calculation on every marking action.
   *
   * Following condition to assert whether marking API can be used:
   *  - Not accessible in SSR env, when window is not defined
   *  - Not accessible in some JSDOM/mocked environments (like jest)
   *  - Not accessible in some browsers where API is not implemented
   */
  public static IS_MARKING_ENABLED: boolean = false;
  public static IS_CONSOLE_ENABLED: boolean = false;
  public static IS_FILE_ENABLED: boolean = false;

  public static readonly PREFIX_STYLE: string = 'color: #bada53';
  public static readonly PREFIX_VIMEO_MARK: string = '@vimeo:';

  /** Global logger singleton. */
  private static GLOBAL: Optional<Logger> = null;

  private readonly prefix: string;
  private readonly isEnabled: boolean = true;

  /**
   * @returns global logger singleton object to do inline printing without construction of new logger
   */
  public static getGlobal(): Logger {
    if (!Logger.GLOBAL) {
      Logger.GLOBAL = new Logger('🪐GLBL');
    }

    return Logger.GLOBAL;
  }

  /**
   * Clear file logs and set string storing buffer as empty.
   */
  public static clearLogs(): void {
    this.LOG_FILE_BUFFER = [];
  }

  /**
   * Log in a file with limited count of lines to save reports with detailed info.
   *
   * @param text - string value to include in current shared file buffer for logging and tracing
   */
  public static logInFile(text: string): void {
    if (Logger.IS_FILE_ENABLED) {
      Logger.LOG_FILE_BUFFER.push(text);

      if (Logger.LOG_FILE_BUFFER.length > Logger.LOG_FILE_BUFFER_LINES_LIMIT) {
        Logger.LOG_FILE_BUFFER.splice(0, Logger.LOG_FILE_BUFFER_SPLICE);
      }
    }
  }

  /**
   * Create mark in performance tab of browser to measure accurate timings of major events.
   *
   * @param mark - mark identifier to show in browser profiling events list
   */
  public static mark(mark: string): void {
    if (this.IS_MARKING_ENABLED) {
      window.performance?.mark(`${Logger.PREFIX_VIMEO_MARK}${mark}`);
    }
  }

  /**
   * @param prefix - text group to use as prefix for all logged messages
   * @param isEnabled - whether logger instance should have log enabled
   */
  public constructor(prefix: string = '', isEnabled?: boolean) {
    this.prefix = `[${prefix}]`;

    if (isEnabled !== undefined) {
      this.isEnabled = isEnabled;
    }
  }

  /**
   * @param args - list of arguments and messages to post in debug log
   */
  public debug(...args: Array<unknown>): void {
    this.logAs('debug', args);
  }

  /**
   * @param args - list of arguments and messages to post in warn log
   */
  public warn(...args: Array<unknown>): void {
    this.logAs('warn', args);
  }

  /**
   * @param args - list of arguments and messages to post in error log
   */
  public error(...args: Array<unknown>): void {
    this.logAs('error', args);
  }

  /**
   * @param args - list of arguments and messages to post in info log
   */
  public info(...args: Array<unknown>): void {
    this.logAs('info', args);
  }

  /**
   * Print structured table in window console.
   *
   * @param args - list of rows to print in the table
   */
  public table(...args: Array<unknown>): void {
    if (Logger.IS_CONSOLE_ENABLED) {
      window.console?.table(...args);
    }
  }

  /**
   * Print mark meta message for debug and profiling.
   */
  public mark(mark: string): void {
    return Logger.mark(mark);
  }

  /**
   * Print starting group meta message inside window console.
   */
  public group(name: string): void {
    if (Logger.IS_CONSOLE_ENABLED && this.isEnabled) {
      window.console.group(name);
    }
  }

  /**
   * Print closing group meta message inside window console.
   */
  public closeGroup(): void {
    if (Logger.IS_CONSOLE_ENABLED && this.isEnabled) {
      window.console.groupEnd();
    }
  }

  /**
   * Generic log method for simple formatting and logging of information with shared logic.
   *
   * @param method - logging method to use for message printing
   * @param args - list of arguments for logging operation
   */
  private logAs(method: string, args: Array<unknown>): void {
    if (this.isEnabled) {
      const date: Date = new Date();

      if (Logger.IS_CONSOLE_ENABLED) {
        window.console[method](
          `%c${date.toLocaleTimeString('en-GB')}:${date.getMilliseconds()} ${this.prefix}`,
          Logger.PREFIX_STYLE,
          ...args,
        );
      }

      if (Logger.IS_FILE_ENABLED) {
        Logger.logInFile(
          `${date.toLocaleTimeString('en-GB')}:${date.getMilliseconds()} [${method}] ${
            this.prefix
          } ${args
            .map((it) =>
              typeof it === 'object' ? JSON.stringify(it, this.getCircularReplacer()) : it,
            )
            .join(' ')}\n`,
        );
      }
    }
  }

  /**
   * @param replacer - value to use for replacement marks
   * @returns factory to process key-values during JSON object serialization
   */
  private getCircularReplacer(replacer: Optional<string> = '~circular~') {
    const seen: WeakSet<AnyObject> = new WeakSet();

    return (_: string, value: unknown) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return replacer;
        }

        seen.add(value);
      }
      return value;
    };
  }
}
